// CompleteFollowUpModal — stateless leaf extracted from the
// `renderCompleteConfirmationModal` render-fn in ContactEditModal.tsx
// (#3293 Pass-2). Holds no useState/useEffect of its own: the orchestrator owns
// every piece of follow-up-scheduler state + the date-picker ref + the
// overlay-click handlers and threads them in as explicit typed props. The JSX
// is byte-identical (closure reads → props); only the surrounding render-fn
// shell changed. Renders inside the same modal portal DOM, so layout +
// getBoundingClientRect positioning of the calendar dropdown are unchanged.
import React from 'react';

type FollowUpSchedule = 'none' | '1week' | '2weeks' | '1month' | 'custom';

export interface CompleteFollowUpModalProps {
  show: boolean;
  contactDisplayName: string;
  nextFollowUpTitle: string;
  setNextFollowUpTitle: (s: string) => void;
  nextFollowUpSchedule: FollowUpSchedule;
  setNextFollowUpSchedule: (s: FollowUpSchedule) => void;
  nextFollowUpDate: string;
  setNextFollowUpDate: (s: string) => void;
  showDatePicker: boolean;
  setShowDatePicker: (b: boolean) => void;
  datePickerViewDate: Date;
  setDatePickerViewDate: (d: Date) => void;
  datePickerRef: React.RefObject<HTMLDivElement | null>;
  completeModalContentRef: React.RefObject<HTMLDivElement | null>;
  onOverlayMouseDown: (e: React.MouseEvent) => void;
  onOverlayClick: (e: React.MouseEvent) => void;
  onComplete: () => void;
  onClose: () => void;
}

export function CompleteFollowUpModal({
  show,
  contactDisplayName,
  nextFollowUpTitle,
  setNextFollowUpTitle,
  nextFollowUpSchedule,
  setNextFollowUpSchedule,
  nextFollowUpDate,
  setNextFollowUpDate,
  showDatePicker,
  setShowDatePicker,
  datePickerViewDate,
  setDatePickerViewDate,
  datePickerRef,
  completeModalContentRef,
  onOverlayMouseDown,
  onOverlayClick,
  onComplete,
  onClose,
}: CompleteFollowUpModalProps) {
  if (!show) return null;

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 20000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  };

  return (
    <div
      style={containerStyle}
      onMouseDown={onOverlayMouseDown}
      onClick={onOverlayClick}
    >
      <div
        ref={completeModalContentRef}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '24px' }}>
          <p style={{
            fontSize: '16px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
            margin: '0 0 20px 0',
          }}>
            Mark this follow-up with <strong>{contactDisplayName || 'this contact'}</strong> as complete and optionally schedule the next touchpoint.
          </p>

          {/* Schedule section */}
          <div>
            {/* Title input */}
            <input
              type="text"
              value={nextFollowUpTitle}
              onChange={(e) => setNextFollowUpTitle(e.target.value)}
              placeholder="Follow Up"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              style={{ background: 'var(--color-surface)', outline: 'none', width: 'calc(100% + 4px)', marginLeft: '-4px', marginBottom: '12px' }}
            />

            {/* Tabs */}
            <div style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '10px',
              marginLeft: '-4px',
            }}>
              {[
                { value: 'none', label: 'None' },
                { value: '1week', label: '1 week' },
                { value: '2weeks', label: '2 weeks' },
                { value: '1month', label: '1 month' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setNextFollowUpSchedule(option.value as any);
                    // Update date based on selection
                    if (option.value === 'none') {
                      setNextFollowUpDate('');
                    } else {
                      const nextDate = new Date();
                      switch (option.value) {
                        case '1week':
                          nextDate.setDate(nextDate.getDate() + 7);
                          break;
                        case '2weeks':
                          nextDate.setDate(nextDate.getDate() + 14);
                          break;
                        case '1month':
                          nextDate.setMonth(nextDate.getMonth() + 1);
                          break;
                      }
                      setNextFollowUpDate(nextDate.toISOString().split('T')[0]);
                    }
                  }}
                  style={{
                    width: '93px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: nextFollowUpSchedule === option.value ? 600 : 500,
                    borderRadius: '6px',
                    border: '2px solid',
                    borderColor: nextFollowUpSchedule === option.value ? '#9ca3af' : '#d1d5db',
                    background: nextFollowUpSchedule === option.value ? '#F1F3F4' : 'white',
                    color: nextFollowUpSchedule === option.value ? '#374151' : '#7a7f85',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s, color 0.15s',
                    boxShadow: nextFollowUpSchedule === option.value ? 'inset 0 0 0 1px #9ca3af' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (nextFollowUpSchedule !== option.value) {
                      e.currentTarget.style.borderColor = '#9ca3af';
                      e.currentTarget.style.color = '#374151';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (nextFollowUpSchedule !== option.value) {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.color = '#7a7f85';
                    }
                  }}
                >
                  {option.label}
                </button>
              ))}

              {/* Custom date picker tab */}
              <div
                ref={datePickerRef}
                style={{
                  width: '93px',
                  position: 'relative',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  style={{
                    width: '93px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: nextFollowUpSchedule === 'custom' ? 600 : 500,
                    borderRadius: '6px',
                    border: '2px solid',
                    borderColor: nextFollowUpSchedule === 'custom' ? '#9ca3af' : '#d1d5db',
                    background: nextFollowUpSchedule === 'custom' ? '#F1F3F4' : 'white',
                    color: nextFollowUpSchedule === 'custom' ? '#374151' : '#7a7f85',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s, color 0.15s',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: nextFollowUpSchedule === 'custom' ? 'inset 0 0 0 1px #9ca3af' : 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (nextFollowUpSchedule !== 'custom') {
                      e.currentTarget.style.borderColor = '#9ca3af';
                      e.currentTarget.style.color = '#374151';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (nextFollowUpSchedule !== 'custom') {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.color = '#7a7f85';
                    }
                  }}
                >
                  {nextFollowUpDate
                    ? new Date(nextFollowUpDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    )}
                </button>

                {/* Calendar dropdown */}
                {showDatePicker && (() => {
                  const daysInMonth = new Date(datePickerViewDate.getFullYear(), datePickerViewDate.getMonth() + 1, 0).getDate();
                  const firstDayOfMonth = new Date(datePickerViewDate.getFullYear(), datePickerViewDate.getMonth(), 1).getDay();
                  const monthName = datePickerViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

                  const selectDate = (day: number) => {
                    const selected = new Date(datePickerViewDate.getFullYear(), datePickerViewDate.getMonth(), day);
                    const year = selected.getFullYear();
                    const month = String(selected.getMonth() + 1).padStart(2, '0');
                    const dayStr = String(selected.getDate()).padStart(2, '0');
                    setNextFollowUpDate(`${year}-${month}-${dayStr}`);
                    setNextFollowUpSchedule('custom');
                    setShowDatePicker(false);
                  };

                  return (
                    <div
                      style={{
                        position: 'fixed',
                        top: datePickerRef.current ? datePickerRef.current.getBoundingClientRect().bottom + 4 : 0,
                        left: datePickerRef.current ? datePickerRef.current.getBoundingClientRect().left : 0,
                        zIndex: 10001,
                        background: 'var(--color-surface)',
                        border: '1px solid #DADCE0',
                        borderRadius: '8px',
                        boxShadow: 'var(--popover-shadow, 0 10px 25px rgba(0, 0, 0, 0.15))',
                        padding: '12px',
                        width: '280px',
                      }}
                    >
                      {/* Calendar header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <button
                          type="button"
                          onClick={() => setDatePickerViewDate(new Date(datePickerViewDate.getFullYear(), datePickerViewDate.getMonth() - 1, 1))}
                          style={{
                            padding: '4px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            borderRadius: '4px',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#F1F3F4'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                        </button>
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>{monthName}</span>
                        <button
                          type="button"
                          onClick={() => setDatePickerViewDate(new Date(datePickerViewDate.getFullYear(), datePickerViewDate.getMonth() + 1, 1))}
                          style={{
                            padding: '4px',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            borderRadius: '4px',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#F1F3F4'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </div>

                      {/* Day headers */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: '4px' }}>
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                          <div key={day} style={{ fontSize: '11px', textAlign: 'center', color: 'var(--color-text-muted)', padding: '4px 0' }}>
                            {day}
                          </div>
                        ))}
                      </div>

                      {/* Calendar days */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                          <div key={`empty-${i}`} style={{ height: '28px' }} />
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                          const day = i + 1;
                          const today = new Date();
                          const isToday = today.getFullYear() === datePickerViewDate.getFullYear() &&
                                         today.getMonth() === datePickerViewDate.getMonth() &&
                                         today.getDate() === day;
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => selectDate(day)}
                              style={{
                                height: '28px',
                                width: '28px',
                                fontSize: '12px',
                                borderRadius: '50%',
                                border: 'none',
                                background: isToday ? 'var(--xavior-blue-bg, #dbeafe)' : 'transparent',
                                color: isToday ? '#1e40af' : '#374151',
                                fontWeight: isToday ? 500 : 400,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              onMouseEnter={(e) => {
                                if (!isToday) e.currentTarget.style.background = '#F1F3F4';
                              }}
                              onMouseLeave={(e) => {
                                if (!isToday) e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Helper text */}
            <p style={{
              fontSize: '16px',
              color: 'var(--color-text-muted)',
              lineHeight: 1.6,
              margin: '20px 0 0 0',
            }}>
              We'll create a new task for the selected date.
            </p>
          </div>
        </div>

        <div style={{
          padding: '8px 24px 16px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: 'var(--color-action-cancel-bg)',
              border: '1px solid var(--color-action-cancel-border)',
              color: 'var(--color-action-cancel-text)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-action-cancel-bg-hover)';
              e.currentTarget.style.borderColor = 'var(--color-action-cancel-border-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-action-cancel-bg)';
              e.currentTarget.style.borderColor = 'var(--color-action-cancel-border)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={onComplete}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: '#16a34a',
              border: 'none',
              color: 'white',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#15803d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#16a34a';
            }}
          >
            Complete
          </button>
        </div>
      </div>
    </div>
  );
}
